import { useState, useEffect } from 'react';
import { Globe, AlertCircle } from 'lucide-react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import type { DateRange, GeoTrafficRow, TrafficSourceRow } from '../../../api/marketing';
import { fetchTraffic } from '../../../api/marketing';
import DateRangeSelector, { daysAgo } from './shared/DateRangeSelector';
import StatCard from './shared/StatCard';

const SOURCE_COLORS: Record<string, string> = {
  organic: '#4A6741',
  cpc: '#3B82F6',
  social: '#8B5CF6',
  email: '#F59E0B',
  referral: '#EC4899',
  direct: '#6B7280',
  '(none)': '#6B7280',
};

function classifyMedium(medium: string): string {
  const m = medium.toLowerCase();
  if (m === 'organic') return 'organic';
  if (m === 'cpc' || m === 'paid') return 'cpc';
  if (m === 'social' || m === 'social-media') return 'social';
  if (m === 'email') return 'email';
  if (m === 'referral') return 'referral';
  return 'direct';
}

export default function TrafficIntelligence() {
  const [dateRange, setDateRange] = useState<DateRange>(daysAgo(30));
  const [geo, setGeo] = useState<GeoTrafficRow[]>([]);
  const [sources, setSources] = useState<TrafficSourceRow[]>([]);
  const [available, setAvailable] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchTraffic(dateRange)
      .then((res) => {
        setAvailable(res.available);
        setMessage(res.message || null);
        setGeo(res.geo);
        setSources(res.sources);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [dateRange]);

  // Aggregate sources by medium type for pie chart
  const sourceAgg = sources.reduce<Record<string, number>>((acc, s) => {
    const key = classifyMedium(s.medium);
    acc[key] = (acc[key] || 0) + s.sessions;
    return acc;
  }, {});

  const pieData = Object.entries(sourceAgg)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  // Top regions for bar chart
  const regionAgg = geo.reduce<Record<string, number>>((acc, g) => {
    if (g.region && g.region !== '(not set)') {
      acc[g.region] = (acc[g.region] || 0) + g.sessions;
    }
    return acc;
  }, {});

  const barData = Object.entries(regionAgg)
    .map(([region, sessions]) => ({ region, sessions }))
    .sort((a, b) => b.sessions - a.sessions)
    .slice(0, 15);

  const totalSessions = sources.reduce((sum, s) => sum + s.sessions, 0);
  const totalUsers = sources.reduce((sum, s) => sum + s.totalUsers, 0);
  const totalConversions = sources.reduce((sum, s) => sum + s.conversions, 0);

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!loading && !available) {
    return (
      <div className="space-y-6">
        <h2 className="text-lg font-semibold text-bark-900 dark:text-stone-100">Traffic Intelligence</h2>
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
          <AlertCircle className="text-amber-500 mx-auto mb-3" size={32} />
          <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">GA4 Not Configured</p>
          <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">{message || 'Add GA4 credentials to enable traffic intelligence.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-bark-900 dark:text-stone-100">Traffic Intelligence</h2>
        <DateRangeSelector value={dateRange} onChange={setDateRange} />
      </div>

      {/* Stats */}
      {loading ? (
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border border-stone-200 dark:border-stone-700 animate-pulse h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          <StatCard label="Sessions" value={totalSessions.toLocaleString()} icon={Globe} iconColor="text-water-600" />
          <StatCard label="Users" value={totalUsers.toLocaleString()} icon={Globe} iconColor="text-moss-600" />
          <StatCard label="Conversions" value={totalConversions.toLocaleString()} icon={Globe} iconColor="text-amber-600" />
        </div>
      )}

      {/* Charts row */}
      {!loading && (
        <div className="grid grid-cols-2 gap-6">
          {/* Pie: Traffic by Source */}
          <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-4">
            <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100 mb-4">Traffic by Source</h3>
            {pieData.length === 0 ? (
              <p className="text-xs text-stone-400 text-center py-8">No traffic data</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry) => (
                      <Cell key={entry.name} fill={SOURCE_COLORS[entry.name] || '#6B7280'} />
                    ))}
                  </Pie>
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts v3 Tooltip formatter types are overly strict */}
                  <Tooltip formatter={((value: number) => value.toLocaleString()) as any} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Bar: Top Regions */}
          <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-4">
            <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100 mb-4">Top Regions by Sessions</h3>
            {barData.length === 0 ? (
              <p className="text-xs text-stone-400 text-center py-8">No geographic data</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={barData} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis
                    type="category"
                    dataKey="region"
                    tick={{ fontSize: 11 }}
                    width={80}
                  />
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts v3 Tooltip formatter types are overly strict */}
                  <Tooltip formatter={((value: number) => value.toLocaleString()) as any} />
                  <Bar dataKey="sessions" fill="#4A6741" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {/* Geographic table */}
      {!loading && geo.length > 0 && (
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 overflow-x-auto">
          <div className="px-4 py-3 border-b border-stone-200 dark:border-stone-700">
            <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100">Geographic Breakdown</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-stone-50 dark:bg-[#1e2520]">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-stone-600 dark:text-stone-400">Region</th>
                <th className="px-4 py-3 text-left font-medium text-stone-600 dark:text-stone-400">City</th>
                <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Sessions</th>
                <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Users</th>
                <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Bounce Rate</th>
                <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Conversions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
              {geo.slice(0, 30).map((row, i) => (
                <tr key={`${row.region}-${row.city}-${i}`} className="hover:bg-moss-50/50 dark:hover:bg-[#242d28]">
                  <td className="px-4 py-2 text-bark-900 dark:text-stone-200">{row.region}</td>
                  <td className="px-4 py-2 text-stone-600 dark:text-stone-400">{row.city}</td>
                  <td className="px-4 py-2 text-right">{row.sessions.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">{row.totalUsers.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">{(row.bounceRate * 100).toFixed(1)}%</td>
                  <td className="px-4 py-2 text-right">{row.conversions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
